import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { FaTicketAlt, FaPlus, FaComments } from 'react-icons/fa';
import './Support.css';

const Support = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    category: 'product',
    priority: 'medium',
    description: ''
  });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await api.get('/support/tickets');
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/support/tickets', formData);
      toast.success('Support ticket created successfully!');
      setShowModal(false);
      setFormData({
        subject: '',
        category: 'product',
        priority: 'medium',
        description: ''
      });
      fetchTickets();
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'open': return 'badge-warning';
      case 'in_progress': return 'badge-info';
      case 'resolved': return 'badge-success';
      case 'closed': return 'badge-secondary';
      default: return 'badge-warning';
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'urgent': return 'badge-danger';
      case 'high': return 'badge-warning';
      case 'medium': return 'badge-info';
      case 'low': return 'badge-secondary';
      default: return 'badge-info';
    }
  };

  if (loading) return <Layout><div className="loading">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="support-page">
        <div className="support-header">
          <div>
            <h1>Support Center</h1>
            <p>Get help with your orders, products, or account</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <FaPlus /> Create Ticket
          </button>
        </div>

        <div className="support-categories card">
          <h3>Quick Help Categories</h3>
          <div className="categories-grid">
            <div className="category-item">
              <h4>Product Support</h4>
              <p>Issues with product listings or details</p>
            </div>
            <div className="category-item">
              <h4>Order Support</h4>
              <p>Help with order tracking and delivery</p>
            </div>
            <div className="category-item">
              <h4>Payment Issues</h4>
              <p>Payment failures or refund queries</p>
            </div>
            <div className="category-item">
              <h4>Account Help</h4>
              <p>Login, profile, or verification issues</p>
            </div>
          </div>
        </div>

        <div className="tickets-section">
          <h2>My Support Tickets</h2>
          {tickets.length === 0 ? (
            <div className="empty-state card">
              <FaTicketAlt style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }} />
              <p>No support tickets yet</p>
              <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                Create Your First Ticket
              </button>
            </div>
          ) : (
            <div className="tickets-list">
              {tickets.map(ticket => (
                <div key={ticket._id} className="ticket-card card">
                  <div className="ticket-header">
                    <div className="ticket-info">
                      <h3>{ticket.subject}</h3>
                      <p className="ticket-number">Ticket #{ticket.ticketNumber}</p>
                    </div>
                    <div className="ticket-badges">
                      <span className={`badge ${getStatusBadgeClass(ticket.status)}`}>
                        {ticket.status.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className={`badge ${getPriorityBadgeClass(ticket.priority)}`}>
                        {ticket.priority.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="ticket-body">
                    <p>{ticket.description}</p>
                  </div>
                  <div className="ticket-footer">
                    <div className="ticket-meta">
                      <span>Category: <strong>{ticket.category}</strong></span>
                      <span>Created: {new Date(ticket.createdAt).toLocaleDateString('en-IN')}</span>
                    </div>
                    <button className="btn btn-outline" onClick={() => navigate(`/support/tickets/${ticket._id}`)}>
                      <FaComments /> View Conversation
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Create Support Ticket</h2>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    className="form-control"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    placeholder="Brief description of your issue"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Category *</label>
                    <select 
                      name="category" 
                      className="form-control" 
                      value={formData.category}
                      onChange={handleInputChange}
                    >
                      <option value="product">Product</option>
                      <option value="order">Order</option>
                      <option value="payment">Payment</option>
                      <option value="finance">Finance</option>
                      <option value="technical">Technical</option>
                      <option value="account">Account</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Priority *</label>
                    <select 
                      name="priority" 
                      className="form-control" 
                      value={formData.priority}
                      onChange={handleInputChange}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    name="description"
                    className="form-control"
                    rows="5"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    placeholder="Please provide detailed information about your issue..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Create Ticket
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Support;
